import { Auth } from "aws-amplify";
import { Pages } from "../constants/enums";
import useAccount from "./useAccount";
import useApp from "./useApp";
import useFeature from "./useFeature";
import useConcept from "./useConcept";
import { DataStore } from "aws-amplify";
import {
  AdminPermissions,
  ListingByAccountVariables,
  ListingVariables,
  UserConceptsListingVariables,
} from "../models/app";
import usePermissions from "./usePermissions";
import useAdminGroup from "./useAdminGroup";
import useUserConcepts from "./useUserConcepts";
import { Concept, UserConcepts } from "../models";

const useAuth = () => {
  const {
    showError,
    showConfirm,
    setSession,
    clearSession,
    navigateTo,
    setPermissions,
  } = useApp();

  const { getAdminPermissions } = usePermissions();
  const { accountsFetchByDomainOnline } = useAccount("accounts", "account");
  const { featuresFetchAll, featuresChangeListing } = useFeature(
    "features",
    "feature",
  );
  const { conceptsFetch, conceptsChangeListing } = useConcept(
    "concepts",
    "concept",
  );
  const { userConceptsFetch, userConceptsChangeListing } = useUserConcepts(
    "userConcepts",
    "userConcept",
  );
  const { adminGroupsFetchCurrentUser } = useAdminGroup(
    "adminGroups",
    "adminGroup",
  );

  async function register(data: any) {
    try {
      await Auth.signUp({
        username: `${data.email}`,
        password: data.password,
        attributes: {
          name: data.name,
          email: `${data.email}`,
        },
      });

      navigateTo("/");
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error Occurred",
      );
      console.log("error signing up:", err);
    }
  }

  async function login(data: any) {
    try {
      const user = await Auth.signIn({
        username: `${data.email}`,
        password: data.password,
      });

      const session = user.signInUserSession.idToken.payload;
      setSession(session);

      DataStore.start();

      const params: ListingVariables = {
        searchText: "",
        startIndex: 0,
        limit: 1000,
      };
      const userConceptParams: UserConceptsListingVariables = {
        ...params,
        userID: session?.sub,
      };

      const result = await Promise.all([
        accountsFetchByDomainOnline(session),
        featuresFetchAll({}),
        userConceptsFetch(userConceptParams),
        adminGroupsFetchCurrentUser(session.sub),
      ]);

      const account = result[0];
      const features = result[1];
      const userConcepts: UserConcepts = result[2][0];
      const userGroup = result[3];

      const listingByAccountParams: ListingByAccountVariables = {
        ...params,
        accountID: account.id,
      };

      const resultData: any[] = await Promise.all([
        conceptsFetch(listingByAccountParams, session),
        getAdminPermissions({
          userGroup,
        }),
      ]);

      const permissions: AdminPermissions = resultData[1];
      const concepts: Concept[] = resultData[0]; // All account concepts

      setSession(session);
      setPermissions(permissions);
      featuresChangeListing(features);
      conceptsChangeListing(concepts);
      userConceptsChangeListing(userConcepts);
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error Occurred",
      );
      console.log("error signing in", err);
    }
  }

  async function logout() {
    try {
      await Auth.signOut();

      clearSession();
      navigateTo("/");
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error Occurred",
      );
      console.log("error signing out: ", err);
    }
  }

  async function forgetPassword(data: any) {
    try {
      // Send confirmation code to user's email
      await Auth.forgotPassword(`${data.email}`);
      showConfirm("Verification code has been sent to your email.");
      navigateTo(`/${Pages.RESET_PASSWORD}`);
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error Occurred",
      );
      console.log("error sending code to email: ", err);
    }
  }

  async function forgetPasswordSubmit(data: any) {
    try {
      const resetPassword = await Auth.forgotPasswordSubmit(
        `${data.email}`,
        data.code,
        data.newPassword,
      );
      return resetPassword;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error Occurred",
      );
      console.log(err);
    }
  }

  return { login, register, logout, forgetPassword, forgetPasswordSubmit };
};

export default useAuth;
